import React from 'react';

import { useSelector } from 'react-redux';

import { STATUS_LOADING } from 'constants/MediaConstants';

import { getMediaIsDefrostingById } from 'modules/medias/selectors';

import { Loader } from '..';
import ArchiveMediaDisplay from './components/ArchivedMediaDisplay';
import AvailableMediaDisplay from './components/AvailableMediaDisplay';
import * as style from './index.module.less';

/**
 * @typedef {{
*   index: number,
*   medias: import('modules/medias/types/abstract-media').AbstractMedia[],
*   onChange?: (index: number) => void,
*   autoPlay?: boolean,
* }} MediaModalContent
*/
/** @type {React.FC<MediaModalContent>} */
const Content = ({ medias = [], onChange, autoPlay = false, index: defaultIndex = 0, error }) => {
  const [index, setIndex] = React.useState(defaultIndex);

  const media = medias[index];
  const isDefrosting = useSelector(state => getMediaIsDefrostingById(state, media));
  const { archived } = media;
  const displayNavigation = medias.length > 1;
  const mediaIsAvailable = !isDefrosting && !archived;
  const isLoading = media.status === STATUS_LOADING;

  const handleNextOnClick = React.useCallback(() => {
    const newIndex = (index + 1) % medias.length;
    setIndex(newIndex);
    onChange?.(newIndex);
  }, [medias, onChange, index]);

  const handlePreviousOnClick = React.useCallback(() => {
    const newIndex = index > 0 ? index - 1 : medias.length - 1;
    setIndex(newIndex);
    onChange?.(newIndex);
  }, [medias, onChange, index]);

  const handleKeyboardEvent = React.useCallback(
    ({ key }) => {
      if (key === 'ArrowRight') {
        handleNextOnClick();
      }

      if (key === 'ArrowLeft') {
        handlePreviousOnClick();
      }
    },
    [handlePreviousOnClick, handleNextOnClick]
  );

  React.useLayoutEffect(() => {
    window.document.addEventListener('keydown', handleKeyboardEvent);
    return () => {
      window.document.removeEventListener('keydown', handleKeyboardEvent);
    };
  }, [handleKeyboardEvent]);

  return (
    <div className={style['modal-body']}>
      {displayNavigation && (
        <React.Fragment>
          <button data-cy="previous" onClick={handlePreviousOnClick} className={style['modal__btn-prev']} type="button">
            <span>
              <i className="icons fb-icon--arrow-left" />
            </span>
          </button>
          <button data-cy="next" onClick={handleNextOnClick} className={style['modal__btn-next']} type="button">
            <span>
              <i className="icons fb-icon--arrow-right" />
            </span>
          </button>
        </React.Fragment>
      )}
      <div>
        {Boolean(error) && error}
        {isLoading && <Loader className="d-flex justify-content-center" />}
        {mediaIsAvailable && <AvailableMediaDisplay media={media} handleOnClick={handleNextOnClick} autoPlay={autoPlay} />}
        {archived && <ArchiveMediaDisplay isDefrosting={isDefrosting} handleOnClick={handleNextOnClick} />}
      </div>
    </div>
  );
};

export default Content;
